'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { pick } from 'underscore';
import * as client from 'DesignData/api/GlobalWidgets';
import * as revisionsClient from 'DesignData/api/Revisions';
import SmartProperties from 'DesignData/enums/SmartProperties';
import { getGlobalWidgetNameFromLabel } from 'DesignData/helpers/globalWidgets';
import GlobalWidgetSummary from 'DesignData/models/widgets/GlobalWidgetSummary';
import * as globalWidgetsSelectors from 'DesignData/selectors/globalWidgets';
import { GLOBAL_WIDGET_DELETE, GLOBAL_WIDGET_EDIT_BODY, GLOBAL_WIDGET_FETCH, GLOBAL_WIDGET_PUBLISH, GLOBAL_WIDGET_RENAME, GLOBAL_WIDGET_REVERT, GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH, GLOBAL_WIDGET_EDIT_SMART_RULES, GLOBAL_WIDGET_REMOVE_SMART_RULES, GLOBAL_WIDGET_UPGRADE, GLOBAL_WIDGET_EDIT_BODY_UNDO, GLOBAL_WIDGET_EDIT_BODY_REDO, GLOBAL_WIDGET_CREATE_FROM_FILE } from 'DesignData/actions/ActionTypes';
const getPastEditHistoryCount = globalWidgetsSelectors.makeGetPastEditHistoryCount();
const getFutureEditHistoryCount = globalWidgetsSelectors.makeGetFutureEditHistoryCount();
/**
 * GET designmanager/v1/widgets/:globalWidgetId
 */

export function fetch(globalWidgetId) {
  const {
    request,
    receive,
    error
  } = fetch;
  return dispatch => {
    dispatch(request(globalWidgetId));
    return client.fetch(globalWidgetId).then(globalWidget => {
      dispatch(receive(globalWidgetId, globalWidget));
    }).catch(err => {
      dispatch(error(globalWidgetId, err));
    });
  };
}

fetch.request = function fetchRequest(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_FETCH,
    globalWidgetId
  };
};

fetch.receive = function fetchReceive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_FETCH,
    globalWidgetId,
    globalWidget
  };
};

fetch.error = function fetchError(globalWidgetId, error) {
  return {
    type: GLOBAL_WIDGET_FETCH,
    globalWidgetId,
    error
  };
};
/**
 * PUT designmanager/v1/widgets/:globalWidgetId
 */


export function publish(globalWidgetId) {
  const {
    request,
    receive,
    error
  } = publish;
  return (dispatch, getState) => {
    dispatch(request(globalWidgetId));
    const state = getState();
    const globalWidget = globalWidgetsSelectors.getGlobalWidget(state, {
      globalWidgetId
    });

    if (globalWidget) {
      client.publish(globalWidgetId, globalWidget.without('hasEdits')).then(__globalWidget => {
        dispatch(receive(globalWidgetId, __globalWidget));
      }).catch(err => {
        dispatch(error(globalWidgetId, err));
      });
      return;
    }

    const refError = new ReferenceError(`Unable to find global widget by ID. Received '${globalWidgetId}'.`);
    dispatch(error(globalWidgetId, refError));
  };
}

publish.request = function publishRequest(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_PUBLISH,
    globalWidgetId
  };
};

publish.receive = function publishReceive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_PUBLISH,
    globalWidgetId,
    globalWidget
  };
};

publish.error = function publishError(globalWidgetId, error) {
  return {
    type: GLOBAL_WIDGET_PUBLISH,
    globalWidgetId,
    error
  };
};
/**
 * Rename global widget
 * POST designmanager/v1/widgets/:globalWidgetId
 */


export function rename(globalWidgetId, label) {
  const {
    request,
    receive,
    error
  } = rename;
  return (dispatch, getState) => {
    const state = getState();
    const globalWidget = globalWidgetsSelectors.getGlobalWidget(state, {
      globalWidgetId
    });
    const name = getGlobalWidgetNameFromLabel(label);
    dispatch(request(globalWidgetId, label, name));
    client.publish(globalWidgetId, globalWidget.merge({
      label,
      name
    })).then(__globalWidget => {
      dispatch(receive(globalWidgetId, __globalWidget));
    }).catch(err => {
      dispatch(error(globalWidgetId, err));
    });
  };
}

rename.request = function renameRequest(globalWidgetId, label, name) {
  return {
    type: GLOBAL_WIDGET_RENAME,
    globalWidgetId,
    label,
    name
  };
};

rename.receive = function renameReceive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_RENAME,
    globalWidgetId,
    globalWidget
  };
};

rename.error = function renameError(globalWidgetId, error) {
  return {
    type: GLOBAL_WIDGET_RENAME,
    globalWidgetId,
    error
  };
};
/**
 * POST designmanager/v1/widgets/:globalWidgetId/versions/restore
 */


export function revert(globalWidgetId, revision) {
  const {
    receive,
    error
  } = revert;
  return dispatch => {
    return revisionsClient.revertGlobalWidget(globalWidgetId, revision.id, true).then(globalWidget => {
      dispatch(receive(globalWidgetId, globalWidget));
    }).catch(__error => {
      dispatch(error(globalWidgetId, __error));
    });
  };
}

revert.receive = function revertReceive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_REVERT,
    globalWidgetId,
    globalWidget
  };
};

revert.error = function revertError(globalWidgetId, error) {
  return {
    type: GLOBAL_WIDGET_REVERT,
    globalWidgetId,
    error
  };
};
/**
 * DELETE designmanager/v1/widgets/:globalWidgetId
 */


export function del(globalWidgetId) {
  const {
    request,
    receive,
    error
  } = del;
  return dispatch => {
    dispatch(request(globalWidgetId));
    return client.del(globalWidgetId).then(() => {
      dispatch(receive(globalWidgetId));
    }).catch(__error => {
      dispatch(error(globalWidgetId, __error));
    });
  };
}

del.request = function deleteRequest(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_DELETE,
    globalWidgetId,
    deleted: false
  };
};

del.receive = function deleteReceive(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_DELETE,
    globalWidgetId,
    deleted: true
  };
};

del.error = function deleteError(globalWidgetId, error) {
  return {
    type: GLOBAL_WIDGET_DELETE,
    globalWidgetId,
    error,
    deleted: false
  };
};
/**
 * Finder sync middleware action
 * @param {(number, number[])} a single template id or array of global widget ids to delete.
 */


export function deleteGlobalWidgets(globalWidgetIds) {
  return {
    type: GLOBAL_WIDGET_DELETE,
    globalWidgetId: globalWidgetIds,
    deleted: true
  };
}
/**
 * GET  designmanager/v1/widgets/list-summaries?includeArchived=false
 */

export function fetchSummaries(query = {}) {
  const {
    request,
    receive,
    error
  } = fetchSummaries;

  const {
    archivedAt = 0,
    limit = 500
  } = query,
        rest = _objectWithoutPropertiesLoose(query, ["archivedAt", "limit"]);

  return dispatch => {
    dispatch(request());
    return client.fetchSummaries(Object.assign({
      archivedAt,
      limit
    }, rest)).then(widgets => {
      dispatch(receive(widgets.objects));
    }).catch(err => {
      dispatch(error(err));
    });
  };
}

fetchSummaries.request = function request() {
  return {
    type: GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH
  };
};

fetchSummaries.receive = function receive(widgets) {
  return {
    type: GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH,
    widgets
  };
};

fetchSummaries.error = function error(err) {
  return {
    type: GLOBAL_WIDGETS_LIST_SUMMARIES_FETCH,
    error: err
  };
};
/**
 * Edit global widget body.
 *
 * @param {Number} globalWidgetId  ID of global widget to edit.
 * @param {Object} edits           Edits to be made to global widget body.
 */


export function editBody(globalWidgetId, edits, merge = true) {
  const {
    request
  } = editBody;
  return (dispatch, getState) => {
    const state = getState();
    const globalWidget = globalWidgetsSelectors.getGlobalWidget(state, {
      globalWidgetId
    });
    const newWidgetBody = merge ? globalWidget.body.merge(edits) : globalWidget.body.replace(edits, {
      deep: true
    });
    dispatch(request(globalWidgetId, newWidgetBody));
  };
}

editBody.request = function editBodyRequest(globalWidgetId, body) {
  return {
    type: GLOBAL_WIDGET_EDIT_BODY,
    globalWidgetId,
    body
  };
};
/*
 * PUT  designmanager/v1/widgets/:widgetId
 */


export function editSmartRules(globalWidget, smartRulesObject, publishWidget = true) {
  const {
    request,
    receive,
    error
  } = editSmartRules;
  const {
    id: globalWidgetId
  } = globalWidget;
  return dispatch => {
    const targetContentType = globalWidget.getTargetContentType();
    const smartProperties = Object.assign({}, pick(smartRulesObject, SmartProperties), {
      [targetContentType]: smartRulesObject[targetContentType]
    });
    const newWidgetBody = globalWidget.body.merge(smartProperties);

    if (!publishWidget) {
      return dispatch(editBody.request(globalWidgetId, newWidgetBody));
    }

    let globalWidgetToSave = globalWidget.set('body', newWidgetBody);

    if (globalWidget instanceof GlobalWidgetSummary) {
      globalWidgetToSave = globalWidgetToSave.set('type', globalWidget.widgetType).without('widgetType');
    }

    dispatch(request(globalWidgetId));
    return client.publish(globalWidgetId, globalWidgetToSave).then(_globalWidget => {
      dispatch(receive(globalWidgetId, _globalWidget));
    }).catch(err => {
      dispatch(error(globalWidgetId, err));
    });
  };
}

editSmartRules.request = function request(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_EDIT_SMART_RULES,
    globalWidgetId
  };
};

editSmartRules.receive = function receive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_EDIT_SMART_RULES,
    globalWidgetId,
    globalWidget
  };
};

editSmartRules.error = function error(globalWidgetId, err) {
  return {
    type: GLOBAL_WIDGET_EDIT_SMART_RULES,
    globalWidgetId,
    error: err
  };
};
/*
 * PUT  designmanager/v1/widgets/:widgetId
 */


export function removeSmartRules(globalWidget, publishWidget = true) {
  const {
    request,
    receive,
    error
  } = removeSmartRules;
  const {
    id: globalWidgetId
  } = globalWidget;
  return dispatch => {
    const newWidgetBody = globalWidget.body.without(SmartProperties);

    if (!publishWidget) {
      return dispatch(editBody.request(globalWidgetId, newWidgetBody));
    }

    let globalWidgetToSave = globalWidget.set('body', newWidgetBody);

    if (globalWidget instanceof GlobalWidgetSummary) {
      globalWidgetToSave = globalWidgetToSave.set('type', globalWidget.widgetType).without('widgetType');
    }

    dispatch(request(globalWidgetId));
    return client.publish(globalWidgetId, globalWidgetToSave).then(_globalWidget => {
      dispatch(receive(globalWidgetId, _globalWidget));
    }).catch(err => {
      dispatch(error(globalWidgetId, err));
    });
  };
}

removeSmartRules.request = function request(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_REMOVE_SMART_RULES,
    globalWidgetId
  };
};

removeSmartRules.receive = function receive(globalWidgetId, globalWidget) {
  return {
    type: GLOBAL_WIDGET_REMOVE_SMART_RULES,
    globalWidgetId,
    globalWidget
  };
};

removeSmartRules.error = function error(globalWidgetId, err) {
  return {
    type: GLOBAL_WIDGET_REMOVE_SMART_RULES,
    globalWidgetId,
    error: err
  };
};

export function upgrade(globalWidgetId) {
  const {
    request,
    receive,
    error
  } = upgrade;
  return dispatch => {
    dispatch(request(globalWidgetId));
    return client.upgrade(globalWidgetId).then(_customWidget => {
      dispatch(receive(globalWidgetId, _customWidget));
    }).catch(err => {
      dispatch(error(globalWidgetId, err));
    });
  };
}

upgrade.request = function request(globalWidgetId) {
  return {
    type: GLOBAL_WIDGET_UPGRADE,
    globalWidgetId
  };
};

upgrade.receive = function receive(globalWidgetId, customWidget) {
  return {
    type: GLOBAL_WIDGET_UPGRADE,
    globalWidgetId,
    customWidget
  };
};

upgrade.error = function error(globalWidgetId, err) {
  return {
    type: GLOBAL_WIDGET_UPGRADE,
    globalWidgetId,
    error: err
  };
};

export function undoEdit(globalWidgetId) {
  return (dispatch, getState) => {
    const pastHistoryCount = getPastEditHistoryCount(getState(), {
      globalWidgetId
    });

    if (pastHistoryCount) {
      dispatch({
        type: GLOBAL_WIDGET_EDIT_BODY_UNDO,
        globalWidgetId
      });
    }
  };
}
export function redoEdit(globalWidgetId) {
  return (dispatch, getState) => {
    const futureHistoryCount = getFutureEditHistoryCount(getState(), {
      globalWidgetId
    });

    if (futureHistoryCount) {
      dispatch({
        type: GLOBAL_WIDGET_EDIT_BODY_REDO,
        globalWidgetId
      });
    }
  };
}
export function createFromFile(globalWidget) {
  return {
    type: GLOBAL_WIDGET_CREATE_FROM_FILE,
    globalWidget
  };
}